
import { useAuth } from "@/hooks/useAuth";
import { PatientList } from "./PatientList";
import { PatientStats } from "./PatientStats";
import { useEffect, useState } from "react";

// Add direct test for Supabase connection
import { supabase } from "@/integrations/supabase/client";

export function PatientManagementDashboard() {
  const { profile } = useAuth();
  const [debugInfo, setDebugInfo] = useState(null);

  // Direct Supabase test
  useEffect(() => {
    async function testSupabaseConnection() {
      console.log("🔍 DEBUG: PatientManagementDashboard mounted");
      console.log("🔍 Profile:", profile);
      
      if (profile?.clinic_id) {
        console.log(`🔍 Testing direct Supabase query for clinic_id: ${profile.clinic_id}`);
        try {
          // 1. Test profiles table access
          const { data: profilesTest, error: profilesError } = await supabase
            .from('profiles')
            .select('count')
            .eq('clinic_id', profile.clinic_id)
            .eq('role', 'patient');
          
          console.log("🔍 Direct profiles query result:", { data: profilesTest, error: profilesError });
          
          // 2. Test profiles raw data with all fields to ensure we're not missing any
          const { data: rawProfiles, error: rawProfilesError } = await supabase
            .from('profiles')
            .select('*')  // Get all fields to see what's actually available
            .eq('clinic_id', profile.clinic_id)
            .eq('role', 'patient')
            .limit(5);
          
          console.log("🔍 Raw profiles data (limited to 5):", JSON.stringify(rawProfiles, null, 2));
          console.log("🔍 Raw profiles error:", rawProfilesError);
          
          // If we have profiles, check their structure
          if (rawProfiles && rawProfiles.length > 0) {
            console.log("🔍 First patient profile structure:");
            console.log("  - Field names:", Object.keys(rawProfiles[0]));
            for (const [key, value] of Object.entries(rawProfiles[0])) {
              console.log(`  - ${key}: ${typeof value} = ${JSON.stringify(value)}`);
            }
          } else {
            console.warn("⚠️ No patient profiles found with query!");
          }
          
          // 3. Test RLS policies by trying another direct query
          const { data: rlsTest, error: rlsError } = await supabase
            .from('triage_submissions')
            .select('count')
            .eq('clinic_id', profile.clinic_id);
          
          console.log("🔍 RLS test on triage_submissions:", { data: rlsTest, error: rlsError });
          
          setDebugInfo({
            profilesCount: profilesTest,
            profilesSample: rawProfiles?.length || 0,
            profilesError: rawProfilesError?.message,
            rlsTest: rlsTest,
            rlsError: rlsError?.message,
            timestamp: new Date().toISOString()
          });
          
        } catch (e) {
          console.error("❌ Catastrophic error in Supabase test:", e);
          setDebugInfo({ error: e.message, timestamp: new Date().toISOString() });
        }
      }
    }
    
    testSupabaseConnection();
  }, [profile]);

  // Only show this dashboard to doctors and staff with a clinic association
  if (!profile?.clinic_id || !['doctor', 'staff'].includes(profile.role)) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
        <p className="text-gray-600">
          You need to be a doctor or staff member associated with a clinic to access patient management.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Patient Management</h1>
        <p className="text-gray-600 mt-2">
          View and manage all patients associated with your clinic
        </p>
        
      </div>

      <PatientStats clinicId={profile.clinic_id} />
      
      <PatientList clinicId={profile.clinic_id} />
    </div>
  );
}
